import {
  Box,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  Icon,
  Input,
  Tag,
  TagLabel,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Button } from "components/ds";
import FilterDropdown from "components/FilterDropdown";
import MemberIdentity from "components/MemberIdentity";
import PloyPopover from "components/PloyPopover";
import { upperFirst } from "modules/utils";
import { useState } from "react";
import { FiCheckCircle, FiTrash2, FiUsers, FiXCircle } from "react-icons/fi";
import { HiOutlineMinusCircle } from "react-icons/hi2";
import { PiNoteBold } from "react-icons/pi";
import { Member } from "state/member";
import { MFAccessReviewAccount } from "state/members/accessReviews";
import MFMembers from "state/members/membersState";
import { Option } from "state/options";

const reviewToColor = (review?: string) => {
  switch (review) {
    case "required":
      return "green";

    case "removed":
    case "not_required":
      return "red";

    case "out_of_scope":
      return "orange";
  }
};

const AccountRow = ({
  roles,
  account,
  canReview,
  isSelected,
  onSelect,
}: {
  canReview: boolean;
  account: MFAccessReviewAccount;
  roles: Option[];
  isSelected: boolean;
  onSelect: (isSelected: boolean) => void;
}) => {
  const { mutate, isLoading } = MFMembers.useReviewAccessReviewAccounts();
  const { isOpen: isOutOfScopeOpen, onToggle: onOutOfScopeToggle } =
    useDisclosure({
      defaultIsOpen: account.review === "out_of_scope",
    });
  const { isOpen: isRemovedOpen, onToggle: onRemovedToggle } = useDisclosure();
  const [outOfScopeReason, setOutOfScopeReason] = useState(
    account.outOfScopeReason || "",
  );
  const [role, setRole] = useState(account.role);
  const [stateNote, setNote] = useState(account.note || "");
  const { mutate: updateAccount, isLoading: isAccountUpdating } =
    MFMembers.useUpdateAccessReviewAccount();

  const {
    isOpen: noteIsOpen,
    onOpen: onNoteOpen,
    onClose: onNoteClose,
  } = useDisclosure();

  const reviewAccount = (review: MFAccessReviewAccount["review"]) => {
    mutate({
      review,
      accessReviewId: account.accessReviewId,
      accessAccountIds: [account.id],
      role: role,
      ...(review === "out_of_scope" ? { outOfScopeReason } : {}),
    });
  };

  const changeRole = (role: string[]) => {
    mutate({
      accessReviewId: account.accessReviewId,
      accessAccountIds: [account.id],
      role: role,
    });
  };

  const handleAccountRemoved = () => {
    reviewAccount("removed");
    onRemovedToggle();
  };

  return (
    <VStack w="full">
      <HStack w="full" spacing={4}>
        <Box w="3%">
          <Checkbox
            isChecked={isSelected}
            onChange={(e) => onSelect(e.target.checked)}
          />
        </Box>
        <Box w="21.5%">
          <MemberIdentity
            member={account.memberAccount.member as Member}
            size="sm"
          />
        </Box>
        <Box w="11.73%">
          <Text fontSize="sm" color="gray.700">
            {account.memberAccount.member?.department}
          </Text>
        </Box>
        <Box w="11.73%">
          <Text fontSize="sm" color="gray.700">
            {account.memberAccount.member?.jobTitle}
          </Text>
        </Box>
        <Box w="25%">
          <FilterDropdown
            initialOptions={roles || []}
            placeholder="Role"
            previewSelection
            createable
            isError={!role?.length}
            icon={FiUsers}
            value={roles.filter((r) =>
              Boolean(role.find((value) => value === r.value)),
            )}
            onChange={(selectedValues) => {
              const values = selectedValues.map(({ value }) => value);
              setRole(values);
              changeRole(values);
            }}
          />
        </Box>
        <HStack w="11%">
          <Text fontSize="sm" color="gray.700">
            {account.lastReview && (
              <Tag colorScheme={reviewToColor(account.lastReview)}>
                <TagLabel>
                  {upperFirst(account.lastReview.replaceAll("_", " "))}
                </TagLabel>
              </Tag>
            )}
          </Text>
        </HStack>
        <HStack w="15%">
          {canReview ? (
            <HStack>
              <Tooltip label="Required">
                <Button
                  size="sm"
                  variant={account.review === "required" ? "solid" : "subtle"}
                  tone="success"
                  isLoading={isLoading}
                  isDisabled={!role.length}
                  onClick={() => reviewAccount("required")}
                  icon={<Icon as={FiCheckCircle} />}
                  aria-label="Mark as required"
                />
              </Tooltip>
              <Tooltip label="Account removed. Select this when you have removed the account from the app/resource. If you haven't or are unable to remove the account yourself, but the member no longer needs then account, then select not required.">
                <Button
                  size="sm"
                  variant={account.review === "removed" ? "solid" : "subtle"}
                  tone="error"
                  onClick={onRemovedToggle}
                  isLoading={isLoading}
                  isDisabled={!role.length}
                  icon={<Icon as={FiTrash2} />}
                  aria-label="Mark as account removed"
                />
              </Tooltip>
              <Tooltip label="Account not required. When the account is not required, but you can't / haven't removed the account.">
                <Button
                  size="sm"
                  variant={
                    account.review === "not_required" ? "solid" : "subtle"
                  }
                  tone="error"
                  onClick={() => reviewAccount("not_required")}
                  isDisabled={!role.length}
                  isLoading={isLoading}
                  icon={<Icon as={FiXCircle} />}
                  aria-label="Mark as account not required"
                />
              </Tooltip>
              <Tooltip label="Out of scope">
                <Button
                  size="sm"
                  variant={
                    account.review === "out_of_scope" ? "solid" : "subtle"
                  }
                  tone="secondary"
                  onClick={onOutOfScopeToggle}
                  isLoading={isLoading}
                  isDisabled={!role.length}
                  icon={<Icon as={HiOutlineMinusCircle} />}
                  aria-label="Mark out of scope"
                />
              </Tooltip>
              <PloyPopover
                isOpen={noteIsOpen}
                onClose={() => onNoteClose()}
                button={
                  <Tooltip label={account.note || "Add note"}>
                    <Button
                      variant="subtle"
                      tone="neutral"
                      size="sm"
                      style={{ opacity: stateNote ? 1 : 0.35 }}
                      onClick={() => onNoteOpen()}
                      icon={<Icon as={PiNoteBold} boxSize="16px" />}
                      aria-label="Add note"
                    />
                  </Tooltip>
                }
              >
                <VStack p={4} spacing={4} align="stretch">
                  <Text>Note:</Text>
                  <Textarea
                    value={stateNote}
                    placeholder="Add a note..."
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <HStack justify="flex-end">
                    <Button
                      size="sm"
                      variant="subtle"
                      tone="neutral"
                      onClick={() => onNoteClose()}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="solid"
                      tone="primary"
                      onClick={() => {
                        if (stateNote) {
                          updateAccount({
                            note: stateNote,
                            accountIds: [account.id],
                          });
                        }

                        onNoteClose();
                      }}
                      isLoading={isAccountUpdating}
                    >
                      Save
                    </Button>
                  </HStack>
                </VStack>
              </PloyPopover>
            </HStack>
          ) : (
            <Flex
              justify="center"
              align="center"
              w="24px"
              h="24px"
              bg={
                account.review === "required"
                  ? "green.100"
                  : account.review === "removed"
                    ? "red.100"
                    : "gray.100"
              }
              borderRadius="100%"
            >
              {account.review === "required" ? (
                <Icon as={FiCheckCircle} color="green.700" boxSize="12px" />
              ) : account.review === "out_of_scope" ? (
                <Icon
                  as={HiOutlineMinusCircle}
                  boxSize="12px"
                  color="gray.700"
                />
              ) : (
                <Icon as={FiXCircle} color="red.700" boxSize="12px" />
              )}
            </Flex>
          )}
        </HStack>
      </HStack>
      <Collapse in={isOutOfScopeOpen} style={{ width: "100%" }}>
        {canReview ? (
          <HStack py={4} w="full">
            <VStack align="flex-start" spacing={1} w="85%">
              <Text fontSize="xs" color="gray.600">
                Reason
              </Text>
              <Input
                size="sm"
                value={outOfScopeReason}
                onChange={(e) => setOutOfScopeReason(e.target.value)}
              />
            </VStack>
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="xs" color="white">
                reason
              </Text>
              <Tooltip
                label={
                  outOfScopeReason.length < 2 ? "Reason required" : undefined
                }
              >
                <Button
                  variant="solid"
                  tone="primary"
                  size="sm"
                  onClick={() => reviewAccount("out_of_scope")}
                  isDisabled={
                    outOfScopeReason.length < 2 ||
                    account.outOfScopeReason === outOfScopeReason
                  }
                >
                  Submit
                </Button>
              </Tooltip>
            </VStack>
          </HStack>
        ) : (
          <HStack pt={2} w="full" justify={"flex-start"} whiteSpace="pre-wrap">
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Out of scope reason:
            </Text>
            <Text fontSize="sm" color="gray.600">
              {account.outOfScopeReason}
            </Text>
          </HStack>
        )}
      </Collapse>
      <Collapse in={isRemovedOpen} style={{ width: "100%" }}>
        <HStack py={4} w="full" justify={"center"} spacing={4}>
          <VStack align="flex-start" spacing={1}>
            <Text fontSize="sm" color="gray.600" fontWeight={500}>
              I Confirm that the account has been removed
            </Text>
          </VStack>
          <VStack align="flex-start" spacing={1}>
            <Button
              variant="solid"
              tone="error"
              size="sm"
              onClick={handleAccountRemoved}
            >
              Confirm
            </Button>
          </VStack>
        </HStack>
      </Collapse>
    </VStack>
  );
};

export default AccountRow;
